import { usePageNavigation } from 'hooks/useNavigation'
import React from 'react'
import { Box, Typography } from '@mui/material'
import { Users, Sparkles, TrendingUp, Target } from 'lucide-react'
import ModernTargetCard from './TargetCard'

export default function ModernTargetAudience(): JSX.Element {
  const { navigateToTargetCreators, navigateToTargetFollowers } = usePageNavigation()

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      {/* Header Section */}
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          mb: 8,
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Icon Badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            mb: 3,
            boxShadow: '0 8px 32px rgba(168, 85, 247, 0.4)',
            animation: 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-10px)' },
            },
          }}
        >
          <Target size={40} color="white" />
        </Box>

        {/* Main Title */}
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #ffffff 0%, #c084fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            fontSize: { xs: '32px', md: '48px' },
            lineHeight: 1.2,
          }}
        >
          Find Your Target Audience
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: { xs: '16px', md: '18px' },
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            maxWidth: '800px',
            margin: '0 auto',
            mb: 4,
          }}
        >
          Understand your followers effortlessly. Get insights to help you
          refine your audience targeting and make your business strategies more informed and effective.
        </Typography>

        {/* Stats/Features Row */}
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          {[
            { icon: Sparkles, label: 'AI-Powered Analysis', color: '#a855f7' },
            { icon: TrendingUp, label: 'Deep Insights', color: '#ec4899' },
            { icon: Users, label: 'Instagram Integration', color: '#8b5cf6' },
          ].map((feature, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: 'rgba(30, 41, 59, 0.5)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
            >
              <feature.icon size={18} style={{ color: feature.color }} />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 600,
                }}
              >
                {feature.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Cards Section */}
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: { xs: '24px', md: '60px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <ModernTargetCard
          icon="followers"
          title="Analyze Your Followers"
          description="Deep insights into your most engaged Instagram followers"
          navigation={navigateToTargetFollowers}
          gradient="pink"
        />
      </Box>

      {/* Bottom Info Section */}
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '80px auto 0',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(88, 28, 135, 0.3) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(1.2)' },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontStyle: 'italic',
            }}
          >
            Currently supporting Instagram • More platforms coming soon
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}